import { forEach, isEmpty, keys } from 'lodash';

import { modelTypeBinaryClassifier } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { OrchestrationInference } from 'interfaces/calculateFeatures';
import { OrchestrationBySlideByType } from 'utils/orchestrationSelections';

export type BinaryClassifierSelectionMode = 'single' | 'multiple';

export const BINARY_CLASSIFIER_DEFERRED_JOIN_MODE = 'deferred';

const binaryClassifierReadAsPrefix = `${modelTypeBinaryClassifier.value}_`;

export const isBinaryClassifierMultipleSelectionKey = (selectionKey?: string): boolean =>
  Boolean(selectionKey?.startsWith(binaryClassifierReadAsPrefix));

export const isBinaryClassifierSelection = (selectionKey: string, modelType?: string): boolean =>
  selectionKey === modelTypeBinaryClassifier.apiModelValue ||
  modelType === modelTypeBinaryClassifier.apiModelValue ||
  isBinaryClassifierMultipleSelectionKey(selectionKey);

export const getBinaryClassifierSelectionKey = (orchestration: Pick<OrchestrationInference, 'orchestrationId'>) =>
  `${binaryClassifierReadAsPrefix}${orchestration.orchestrationId}`;

export const hasBinaryClassifierMultipleSelections = (selectedOrchestrations: OrchestrationBySlideByType): boolean => {
  let hasMultipleSelections = false;

  forEach(selectedOrchestrations, (orchestrationsByType) => {
    if (keys(orchestrationsByType).some((selectionKey) => isBinaryClassifierMultipleSelectionKey(selectionKey))) {
      hasMultipleSelections = true;
      return false;
    }
  });

  return hasMultipleSelections;
};

export const normalizeBinaryClassifierSelectionsForMode = (
  selectedOrchestrations: OrchestrationBySlideByType,
  mode: BinaryClassifierSelectionMode
): OrchestrationBySlideByType => {
  const normalizedSelections: OrchestrationBySlideByType = { ...selectedOrchestrations };

  forEach(selectedOrchestrations, (orchestrationsByType, slideId) => {
    const nextOrchestrationsByType = { ...orchestrationsByType };
    const baseSelection = nextOrchestrationsByType[modelTypeBinaryClassifier.apiModelValue];
    const multipleSelectionKeys = keys(nextOrchestrationsByType).filter((selectionKey) =>
      isBinaryClassifierMultipleSelectionKey(selectionKey)
    );

    if (mode === 'multiple') {
      if (baseSelection?.orchestration?.orchestrationId) {
        nextOrchestrationsByType[getBinaryClassifierSelectionKey(baseSelection.orchestration)] = baseSelection;
        delete nextOrchestrationsByType[modelTypeBinaryClassifier.apiModelValue];
      }
    } else {
      if (!baseSelection && multipleSelectionKeys.length > 0) {
        nextOrchestrationsByType[modelTypeBinaryClassifier.apiModelValue] =
          nextOrchestrationsByType[multipleSelectionKeys[0]];
      }

      forEach(multipleSelectionKeys, (selectionKey) => {
        delete nextOrchestrationsByType[selectionKey];
      });
    }

    normalizedSelections[slideId] = nextOrchestrationsByType;

    if (isEmpty(nextOrchestrationsByType)) {
      delete normalizedSelections[slideId];
    }
  });

  return normalizedSelections;
};
